/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Label } from '../ui/Label';
import { Button } from '../ui/Button';
import { CustomSelect } from '../ui/Select';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  headers: string[];
  requiredColumns: string[];
  autoMapping: Record<string, string | null>;
  onSubmit: (mapping: Record<string, string | null>) => void;
}

export const ColumnMappingModal = ({
  isOpen,
  onClose,
  headers,
  requiredColumns,
  autoMapping,
  onSubmit,
}: Props) => {
  const [mapping, setMapping] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (isOpen) {
      const initialMap: Record<string, string | null> = {};
      requiredColumns.forEach((col) => {
        initialMap[col] = autoMapping[col] ?? null;
      });
      setMapping(initialMap);
    }
  }, [isOpen, autoMapping, requiredColumns]);

  const handleChange = (column: string, selected: string | null) => {
    setMapping((prev) => ({ ...prev, [column]: selected === '' ? null : selected }));
  };

  const handleSubmit = () => {
    if (!mapping.Name) return; 
    onSubmit(mapping);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Map Required Columns">
      <p className="mb-6 text-sm text-muted-foreground">
        Some required fields were not auto-detected. Please map each manually.
      </p>

      {requiredColumns.map((col) => {
        const showNone = ['ID', 'Location', 'Country'].includes(col);
        const options = showNone
          ? [{ label: 'None', value: '' }, ...headers.map((h) => ({ label: h, value: h }))]
          : headers.map((h) => ({ label: h, value: h }));

        return (
          <div key={col} className="mb-4">
            <Label className="mb-1 text-sm font-medium">{col}</Label>
            <CustomSelect
              placeholder={`Select column for ${col}`}
              data={options}
              value={mapping[col] || ''}
              onChange={(val) => handleChange(col, val)}
            />
          </div>
        );
      })}

      <div className="flex justify-end gap-2 mt-8">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Confirm</Button>
      </div>
    </Modal>
  );
};
